import type { StrategyListRead } from '@/types/generated';
import type { StrategyStatus } from '@/types/generated';

// The pills in display order; the route's search schema validates against the same tuple.
export const STRATEGY_LIST_FILTERS = [
  'all',
  'drafts',
  'live',
  'awaiting_review',
  'paused',
  'archived',
] as const;

export type StrategyListFilter = (typeof STRATEGY_LIST_FILTERS)[number];

// Drafts are surfaced only under the dedicated Drafts pill — they carry no
// performance metrics, so they're excluded from every other view and the KPIs.
const NON_ARCHIVED: readonly StrategyStatus[] = [
  'PENDING',
  'ACTIVE',
  'PARTIALLY_ACTIVE',
  'PAUSED',
  'ERROR',
] as const;

const LIVE_STATUSES = new Set<StrategyStatus>(['ACTIVE', 'PARTIALLY_ACTIVE']);

export interface StrategyListServerQuery {
  status: readonly StrategyStatus[];
  awaiting_review?: boolean;
}

// The server owns the review predicate, so the review pill and Paused send its flag.
export const serverQueryFor = (
  filter: StrategyListFilter,
): StrategyListServerQuery => {
  switch (filter) {
    case 'all':
      return { status: NON_ARCHIVED };
    case 'drafts':
      return { status: ['DRAFT'] };
    case 'live':
      return { status: ['ACTIVE', 'PARTIALLY_ACTIVE'] };
    case 'awaiting_review':
      return { status: [], awaiting_review: true };
    case 'paused':
      return { status: ['PAUSED'], awaiting_review: false };
    case 'archived':
      return { status: ['ARCHIVED'] };
  }
};

export const matchesListFilter = (
  item: StrategyListRead,
  filter: StrategyListFilter,
): boolean => {
  switch (filter) {
    case 'all':
      return item.status !== 'ARCHIVED' && item.status !== 'DRAFT';
    case 'drafts':
      return item.status === 'DRAFT';
    case 'live':
      return LIVE_STATUSES.has(item.status);
    case 'awaiting_review':
      return item.awaiting_review;
    case 'paused':
      return item.status === 'PAUSED' && !item.awaiting_review;
    case 'archived':
      return item.status === 'ARCHIVED';
  }
};
